<template>
  <notification-inbox
    :loading="loading"
    :notifications="notifications"
    :selected-notification="selectedNotification"
    :current-page="pagination.currentPage"
    :total-pages="pagination.size"
    :unread-count="unreadCount"
    :categories="categories"
    :active-category="activeCategory"
    :unread-only="unreadOnly"
    :theme="theme"
    :voted="selectedNotification ? !!votedById[selectedNotification.id] : false"
    :voting-busy="votingBusy"
    :general-class-answered="
      selectedNotification
        ? !!generalClassAnsweredById[selectedNotification.id]
        : false
    "
    :general-class-busy="generalClassBusy"
    :dispute-answered="
      selectedNotification ? !!disputedById[selectedNotification.id] : false
    "
    :dispute-busy="disputeBusy"
    :trial-answered="
      selectedNotification
        ? !!trialAnsweredById[selectedNotification.id]
        : false
    "
    :trial-busy="trialBusy"
    :title="$t('inbox.title')"
    :subtitle="$t('notificationsPage.customerSubtitle')"
    @select="openNotification"
    @category-change="changeCategory"
    @toggle-read="toggleRead"
    @unread-only-change="changeUnreadOnly"
    @vote="vote"
    @dispute-class-change="disputeClassChange"
    @confirm-general-class="confirmGeneralClass"
    @decline-general-class="declineGeneralClass"
    @resolve-trial="resolveTrial"
    @respond-trial-suggestion="respondTrialSuggestion"
    @page-change="handlePageChange"
  />
</template>

<script>
import NotificationInbox from "@/components/notifications/NotificationInbox.vue";
import { classThemes } from "@/shared/classThemes";
import pitchCustomerApi from "@/shared/pitchCustomerApi";

const PAGE_SIZE = 10;

export default {
  name: "NotificationsArea",

  components: {
    NotificationInbox,
  },

  data() {
    return {
      allNotifications: [],
      categories: [],
      activeCategory: null,
      unreadOnly: false,
      // Vem da API: com filtro ativo a lista tem só uma categoria e contar
      // as não lidas daqui mostraria menos do que o aluno realmente tem.
      unreadCount: 0,
      selectedId: null,
      loading: true,
      votingBusy: false,
      votedById: {},
      generalClassBusy: false,
      generalClassAnsweredById: {},
      disputeBusy: false,
      disputedById: {},
      trialBusy: false,
      trialAnsweredById: {},
      pagination: {
        currentPage: 1,
        size: 1,
      },
    };
  },

  computed: {
    notifications() {
      const start = (this.pagination.currentPage - 1) * PAGE_SIZE;
      return this.allNotifications.slice(start, start + PAGE_SIZE);
    },
    selectedNotification() {
      return (
        this.notifications.find((item) => item.id === this.selectedId) || null
      );
    },
    theme() {
      return classThemes.customer;
    },
  },

  async mounted() {
    // Card 86akj8ptp: o box de notificações da página inicial abre a
    // notificação clicada (?id=). As fixadas e as mais recentes estão na
    // primeira página -- as mesmas que o box mostra.
    const wanted = this.$route.query?.id || null;
    await this.getNotifications(wanted);
    const item = wanted
      ? this.notifications.find((entry) => entry.id === wanted)
      : null;
    if (item) this.openNotification(item);
  },

  methods: {
    async getNotifications(preferredId = this.selectedId) {
      this.loading = true;

      try {
        const res = await pitchCustomerApi.getNotifications(
          this.activeCategory,
          this.unreadOnly
        );
        this.allNotifications = res.data?.data || [];
        this.categories = res.data?.categories || [];
        this.unreadCount =
          res.data?.unread ??
          this.allNotifications.filter((item) => !item.read).length;
        this.pagination.size =
          Math.max(1, Math.ceil(this.allNotifications.length / PAGE_SIZE)) || 1;
        this.syncSelection(preferredId);
        this.updateStore();
      } catch {
        this.$root.$emit("display-alert", {
          text: this.$t("notificationsPage.loadFailed"),
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    syncSelection(preferredId = null) {
      if (!this.notifications.length) {
        this.selectedId = null;
        return;
      }

      const match = this.notifications.find((item) => item.id === preferredId);
      this.selectedId = (match || this.notifications[0]).id;
    },
    async openNotification(item) {
      this.selectedId = item.id;

      if (!item.read) {
        try {
          await pitchCustomerApi.readNotification(item.id);
          item.read = true;
          // Badge da aba e total descem junto, sem recarregar a lista.
          this.unreadCount = Math.max(0, this.unreadCount - 1);
          this.discountCategory(item.category);
          this.updateStore();
        } catch {
          this.$root.$emit("display-alert", {
            text: this.$t("notificationsPage.readFailed"),
            type: 1,
          });
        }
      }
    },
    handlePageChange(page) {
      this.pagination.currentPage = page;
      this.syncSelection();
    },
    // Trocar de aba é uma busca nova: o filtro é do servidor, então a lista
    // e a paginação voltam ao começo.
    changeCategory(category) {
      if (category === this.activeCategory) return;

      this.activeCategory = category;
      this.pagination.currentPage = 1;
      this.selectedId = null;
      this.getNotifications(null);
    },
    // Retorno de 30/10: o destinatário também marca como lida ou não lida na
    // mão. Abrir a notificação continua marcando como lida sozinho.
    async toggleRead(item) {
      const wasRead = !!item.read;

      try {
        if (wasRead) await pitchCustomerApi.unreadNotification(item.id);
        else await pitchCustomerApi.readNotification(item.id);

        item.read = !wasRead;
        const delta = wasRead ? 1 : -1;
        this.unreadCount = Math.max(0, this.unreadCount + delta);
        const entry = this.categories.find((c) => c.key === item.category);
        if (entry) entry.unread = Math.max(0, entry.unread + delta);
        this.updateStore();
      } catch {
        this.$root.$emit("display-alert", {
          text: this.$t("notificationsPage.readFailed"),
          type: 1,
        });
      }
    },
    // O filtro "não lidas" é do servidor, como a categoria: volta ao começo.
    changeUnreadOnly(value) {
      if (value === this.unreadOnly) return;

      this.unreadOnly = value;
      this.selectedId = null;
      this.pagination.currentPage = 1;
      this.getNotifications(null);
    },
    discountCategory(key) {
      const entry = this.categories.find((item) => item.key === key);
      if (entry) entry.unread = Math.max(0, entry.unread - 1);
    },
    updateStore() {
      this.$store.dispatch("setNotificationsCount", this.unreadCount);
    },
    async vote({ voteId, option }) {
      const currentId = this.selectedId;
      this.votingBusy = true;

      try {
        const res = await pitchCustomerApi.vote(voteId, option);
        this.$set(this.votedById, currentId, true);
        this.$root.$emit("display-alert", {
          text: res.data.message,
          type: 0,
        });
        await this.getNotifications(currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.voteFailed"),
          type: 1,
        });
      } finally {
        this.votingBusy = false;
      }
    },
    // O professor mudou a aula e a mudança já valeu; aqui o aluno registra que
    // discorda e por quê. Quem decide o que fazer é o professor.
    async disputeClassChange({ eventId, justification }) {
      const currentId = this.selectedId;
      this.disputeBusy = true;

      try {
        const res = await pitchCustomerApi.disputeClassChange(
          eventId,
          justification
        );
        this.$set(this.disputedById, currentId, true);
        this.$root.$emit("display-alert", {
          text: res.data.message,
          type: 0,
        });
        await this.getNotifications(currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.disputeFailed"),
          type: 1,
        });
      } finally {
        this.disputeBusy = false;
      }
    },
    // Resposta do aluno depois da aula experimental (card 86ak6kzgj). Dizer
    // "quero continuar" não cria a turma: quem monta continua sendo o
    // professor, que é avisado do interesse.
    async resolveTrial({ classId, interested }) {
      const currentId = this.selectedId;
      this.trialBusy = true;

      try {
        const res = await pitchCustomerApi.resolveTrialInterest(
          classId,
          interested
        );
        this.$set(this.trialAnsweredById, currentId, true);
        this.$root.$emit("display-alert", {
          text:
            res.data?.message ||
            (interested
              ? this.$t("notificationsPage.interested")
              : this.$t("notificationsPage.notInterested")),
          type: 0,
        });
        await this.getNotifications(currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.answerFailed"),
          type: 1,
        });
      } finally {
        this.trialBusy = false;
      }
    },
    // Card 86aje36nz: o professor sugeriu outra data. Confirmar agenda a aula;
    // cancelar encerra a solicitação e libera o aluno para pedir outra.
    async respondTrialSuggestion({ classId, accept }) {
      const currentId = this.selectedId;
      this.trialBusy = true;

      try {
        await pitchCustomerApi.respondTrialSuggestion(classId, accept);
        this.$set(this.trialAnsweredById, currentId, true);
        this.$root.$emit("display-alert", {
          text: accept
            ? this.$t("notificationsPage.trialAccepted")
            : this.$t("notificationsPage.trialDeclined"),
          type: 0,
        });
        await this.getNotifications(currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.answerFailed"),
          type: 1,
        });
      } finally {
        this.trialBusy = false;
      }
    },
    async confirmGeneralClass({ sessionId }) {
      const currentId = this.selectedId;
      this.generalClassBusy = true;

      try {
        await pitchCustomerApi.participateInGeneralClass(sessionId);
        this.$set(this.generalClassAnsweredById, currentId, true);
        this.$root.$emit("display-alert", {
          text: this.$t("notificationsPage.attendanceConfirmed"),
          type: 0,
        });
        await this.getNotifications(currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.attendanceFailed"),
          type: 1,
        });
      } finally {
        this.generalClassBusy = false;
      }
    },
    async declineGeneralClass({ sessionId }) {
      const currentId = this.selectedId;
      this.generalClassBusy = true;

      try {
        await pitchCustomerApi.declineGeneralClass(sessionId);
        this.$set(this.generalClassAnsweredById, currentId, true);
        this.$root.$emit("display-alert", {
          text: this.$t("notificationsPage.answerRecorded"),
          type: 0,
        });
        await this.getNotifications(currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.answerFailedShort"),
          type: 1,
        });
      } finally {
        this.generalClassBusy = false;
      }
    },
  },
};
</script>
